// Behaviour for the lead popup (snippets/sw-lead-popup.liquid).
//
// SINCE 2026-09-10 THIS IS THE DOOR IN FRONT OF THE CHECKOUT, not just an exit catcher. Every
// buy.stripe.com link on the site is intercepted: we take the name, shop, email and number, THEN
// hand the person to Stripe with their email prefilled. Nobody reaches the card form anonymously,
// so a click that does not end in a sale still leaves somebody to ring. It also gives the Meta
// pixel and Google an on-site Lead event, which is the only conversion either platform can see
// while the checkout lives on Stripe's domain.
//
// A CAPTURED PERSON IS NEVER ASKED TWICE. Once localStorage says sent, buy links pass straight
// through with the email prefilled. Standing between a buyer and the card form is the one failure
// this file must never have.
//
// The rules it has to respect, in order of how annoyed somebody gets when you break them:
//
//   1. Never interrupt a conversion. On /pages/pricing the person is reading the thing that takes
//      their money, so the popup only appears on the way OUT. Everywhere else a timer and a scroll
//      depth also count, because someone who has read half the page is interested.
//   2. Never show twice. Dismissed is quiet for two weeks, sent is quiet forever. Both live in
//      localStorage, which is per browser and is the most this site is willing to track.
//   3. Never show on the page that already IS the form (/pages/early-access), and never in the
//      theme editor, where a dialog over the canvas makes the site look broken.
//
// The submit posts to the platform API and to Shopify's contact form, the same pair as
// sw-waitlist-relay.js. The API answer is what the person sees; the Shopify post is the fallback
// that puts the lead in an inbox if the platform is mid-deploy. Losing a lead is the worst thing
// this site can do, so it is worth sending twice and de-duplicating later.
(function () {
  'use strict';

  // Build marker. Shopify's edge serves a random mix of pre- and post-push assets for minutes
  // after a deploy, so a verifier that does not pin the build reports cache noise as code bugs.
  // That happened here: one run failed the scroll trigger and the bot gate against stale JS.
  window.__swLeadPopup = 6;

  var ENDPOINT = 'https://app.sendwrench.com/api/waitlist';
  // How long we hold a buyer while the lead saves. The Shopify contact post is fire-and-forget and
  // the API write is one insert, but a slow network must not park somebody in front of a spinner
  // on the way to giving us money: whichever finishes first, they go.
  var HANDOFF_MS = 2500;
  var KEY = 'sw_leadpop';
  var QUIET_DAYS = 14;
  var DWELL_MS = 30000;
  var SCROLL_AT = 0.55;
  var MIN_DWELL_MS = 3500;

  if (window.Shopify && window.Shopify.designMode) return;

  var dlg = document.getElementById('sw-leadpop');
  if (!dlg || typeof dlg.showModal !== 'function') return;

  var path = location.pathname.replace(/\/+$/, '') || '/';
  if (/\/pages\/early-access$/.test(path)) return;   // the full form is already on the page
  var exitOnly = /\/pages\/pricing$/.test(path);

  // ---- has it already had its turn? -----------------------------------------------------------
  function state() {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
  }
  function remember(v) {
    try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode; it just shows again */ }
  }
  var st = state();
  // `sent` means we already have their details. They are never asked again, but their buy links
  // still get the email prefilled on the way through, which is why this returns AFTER wiring the
  // pass-through rather than before it.
  var captured = !!st.sent;
  var quiet = !!(st.dismissed_at && Date.now() - st.dismissed_at < QUIET_DAYS * 864e5);

  // ---- opening --------------------------------------------------------------------------------
  var form = dlg.querySelector('[data-sw-leadpop-form]');
  var errBox = dlg.querySelector('[data-sw-leadpop-error]');
  var submitBtn = dlg.querySelector('[data-sw-leadpop-submit]');
  var stepAsk = dlg.querySelector('[data-sw-leadpop-step="ask"]');
  var stepDone = dlg.querySelector('[data-sw-leadpop-step="done"]');
  // Timed from page load, NOT from the dialog opening. The endpoint drops anything submitted
  // within two seconds as a bot, silently and with a 200, so a person on browser autofill who
  // taps through four fields in one gesture would have their lead accepted and never stored.
  // The popup cannot even appear before 30s of dwell or half a page of scrolling, so measuring
  // from load is both safer and a truer answer to the question the gate is asking.
  var startedAt = Date.now();
  var opened = false;
  var lastFocus = null;
  // Set when the popup was opened by someone clicking a plan's buy button: where they were going
  // and which plan it was. Null when the popup opened by itself, and then they choose at the end.
  var pending = null;

  function open() {
    if (opened || dlg.open) return;
    // Never in the first few seconds. Exit intent can fire the instant a pointer flicks to the
    // tab bar, and the endpoint discards anything submitted within two seconds of page load as a
    // bot, silently and with a 200, so an instant popup plus browser autofill could produce a lead
    // that looks sent, shows the thank-you, and was never stored. A flick that fast is a bounce
    // rather than a buyer anyway, so waiting costs nothing and closes the hole.
    if (Date.now() - startedAt < MIN_DWELL_MS) return;
    opened = true;
    lastFocus = document.activeElement;
    dlg.showModal();
    var first = dlg.querySelector('input[name="contact[name]"]');
    if (first) first.focus();
    teardown();
  }

  /**
   * THE HAND-OFF.
   *
   * Stripe payment links take `prefilled_email`, so the person does not type their address twice,
   * and `client_reference_id`, which is how a completed checkout is tied back to the ad click that
   * produced it (sw-click-attribution.liquid parks the id on arrival). Both are appended here
   * rather than trusted to already be on the href, because this URL can come from a click we
   * intercepted before the attribution snippet's observer had touched it.
   */
  function checkoutUrl(href, email) {
    if (!href) return null;
    var url = href;
    var clickId = (document.cookie.match('(^|;)\\s*sw_click_id\\s*=\\s*([^;]+)') || [])[2];
    if (clickId) {
      clickId = decodeURIComponent(clickId);
      // Stripe rejects the whole URL on a malformed reference, which would break the buy button
      // rather than just the tracking. Anything questionable is dropped instead.
      if (clickId.length <= 190 && /^[A-Za-z0-9_-]+$/.test(clickId) && url.indexOf('client_reference_id=') === -1) {
        url += (url.indexOf('?') > -1 ? '&' : '?') + 'client_reference_id=' + clickId;
      }
    }
    if (email && url.indexOf('prefilled_email=') === -1) {
      url += (url.indexOf('?') > -1 ? '&' : '?') + 'prefilled_email=' + encodeURIComponent(email);
    }
    return url;
  }

  /**
   * TELL THE AD PLATFORMS SOMETHING HAPPENED.
   *
   * The checkout is on Stripe's domain, so neither pixel can ever see the sale. This form submit
   * is the strongest on-site signal that exists, and until the Stripe webhook posts to the
   * conversions API it is the only thing Meta can optimise against. Wrapped because a blocked
   * pixel must never stop a buyer reaching the card form.
   */
  function reportLead(plan) {
    try { if (window.fbq) window.fbq('track', 'Lead', { content_name: plan || 'Trial capture', currency: 'USD', value: 0 }); } catch (e) {}
    try { if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'trial', event_label: plan || 'popup' }); } catch (e) {}
    try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: 'sw_trial_lead', plan: plan || null }); } catch (e) {}
  }

  function close(reason) {
    if (reason === 'dismissed') remember({ dismissed_at: Date.now() });
    if (dlg.open) dlg.close();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // WHICH THING ACTUALLY SCROLLS CHANGES WITH THE BREAKPOINT, and getting this wrong kills the
  // trigger silently. On desktop html and body are overflow:hidden and an inner .page-wrapper
  // scrolls, so a window listener never fires. Below the breakpoint that flips: .page-wrapper
  // goes overflow:visible with max 0 and the document scrolls normally. Hard-coding either one
  // leaves the trigger dead on the other, and on a phone there is no exit intent to fall back on,
  // so mobile would have been left with the 30s timer alone. Resolve it per event instead.
  var wrapper = document.querySelector('.page-wrapper');

  function progress() {
    var els = [wrapper, document.scrollingElement || document.documentElement];
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el) continue;
      var max = (el.scrollHeight || 0) - (el.clientHeight || 0);
      if (max > 0) return (el.scrollTop || window.scrollY || 0) / max;
    }
    return 0;
  }

  function onScroll() {
    if (progress() >= SCROLL_AT) open();
  }
  function onExit(e) {
    // Only a real exit through the top of the window, not a drift into the dev tools or a
    // subwindow. relatedTarget is null when the pointer actually leaves the document.
    if (e.clientY <= 0 && !e.relatedTarget) open();
  }

  var timer = null;
  function teardown() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (wrapper) wrapper.removeEventListener('scroll', onScroll);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExit);
  }

  /**
   * EVERY BUY BUTTON GOES THROUGH HERE FIRST (Kevin, 2026-09-10).
   *
   * Capture on the way to the card, not instead of it. A person who has already been captured is
   * waved straight through with their email prefilled: a form standing between a buyer and the
   * checkout would cost far more than the lead is worth.
   */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href*="buy.stripe.com"]') : null;
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    var href = a.getAttribute('href') || '';
    var plan = a.getAttribute('data-sw-plan')
      || (a.closest('[data-plan-card]') && (a.closest('[data-plan-card]').querySelector('.sw-plan__name') || {}).textContent)
      || '';
    plan = (plan || '').trim();

    if (captured) {
      var known = checkoutUrl(href, st.email || '');
      if (known && known !== href) { e.preventDefault(); location.href = known; }
      return;                                   // already ours: never ask twice
    }
    if (dlg.open) return;                       // the popup's own plan buttons live here too

    e.preventDefault();
    pending = { href: href, plan: plan };
    opened = false;                             // a deliberate click outranks the dwell rules
    var wasStarted = startedAt;
    startedAt = Math.min(wasStarted, Date.now() - MIN_DWELL_MS - 1);   // no artificial wait on an intentional click
    open();
    var lede = dlg.querySelector('[data-sw-leadpop-lede]');
    if (lede && plan) {
      lede.innerHTML = 'One step before the card. Tell us who you are and we will set up your <strong>'
        + plan.replace(/[<>&]/g, '') + '</strong> trial. Thirty days free, we move your existing data '
        + 'across for you, and nothing is charged until day 31.';
    }
  }, true);

  document.addEventListener('mouseout', onExit);
  if (captured || quiet) {
    // No self-opening: they have either given us their details or told us not to ask. The click
    // interception above is still live, which is the part that matters.
  } else if (!exitOnly) {
    timer = setTimeout(open, DWELL_MS);
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Esc fires dialog's own close event; count that as a dismissal like any other.
  dlg.addEventListener('cancel', function (e) { e.preventDefault(); close('dismissed'); });
  Array.prototype.forEach.call(dlg.querySelectorAll('[data-sw-leadpop-close]'), function (b) {
    b.addEventListener('click', function () { close('dismissed'); });
  });
  // Clicking the backdrop is a dismissal. The card stops the bubble, so a click that reaches the
  // dialog element itself landed outside it.
  dlg.addEventListener('click', function (e) { if (e.target === dlg) close('dismissed'); });

  // ---- sending --------------------------------------------------------------------------------
  function val(name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el && el.value ? el.value.trim() : null;
  }
  function fail(msg, name) {
    errBox.textContent = msg;
    errBox.hidden = false;
    var el = name && form.querySelector('[name="' + name + '"]');
    if (el) { el.setAttribute('aria-invalid', 'true'); el.focus(); }
  }

  // Shopify's own contact form, posted in the background so the team gets the email too. Fired and
  // forgotten on purpose: it must never delay or block what the person sees.
  function alsoEmailKevin(d) {
    try {
      var body = new URLSearchParams();
      body.set('form_type', 'contact');
      body.set('utf8', '✓');
      body.set('contact[name]', d.name || '');
      body.set('contact[email]', d.email || '');
      body.set('contact[phone]', d.phone || '');
      body.set('contact[shop name]', d.shop_name || '');
      body.set('contact[techs]', d.techs || '');
      body.set('contact[form]', 'Trial start (popup)');
      body.set('contact[body]',
        'Asked for a free trial from the popup on ' + (d.page || '') + '. Plan: ' +
        ((pending && pending.plan) || 'not chosen yet') + '. Texts: ' + (d.sms_consent ? 'yes' : 'no') +
        '. If they do not appear as a shop within the hour, the card form is where they stopped.');
      fetch('/contact', { method: 'POST', body: body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
        .catch(function () {});
    } catch (e) { /* never let the fallback break the real submit */ }
  }

  var finished = false;
  /**
   * Captured. Now get out of the way: fire the one conversion signal the ad platforms can see,
   * then either hand them straight to the checkout they were already heading for, or show the
   * three plans if the popup caught them before they picked one.
   */
  function finish(payload) {
    if (finished) return;
    finished = true;
    remember({ sent: true, email: payload.email, at: Date.now() });
    captured = true;
    reportLead(pending && pending.plan);

    stepAsk.hidden = true;
    stepDone.hidden = false;

    var contBlock = stepDone.querySelector('[data-sw-leadpop-continue]');
    var plansBlock = stepDone.querySelector('[data-sw-leadpop-plans]');
    var goBtn = stepDone.querySelector('[data-sw-leadpop-go]');

    if (pending && pending.href) {
      var dest = checkoutUrl(pending.href, payload.email);
      if (goBtn) goBtn.setAttribute('href', dest);
      var nameEl = stepDone.querySelector('[data-sw-leadpop-planname]');
      if (nameEl && pending.plan) nameEl.textContent = pending.plan;
      if (contBlock) contBlock.hidden = false;
      if (plansBlock) plansBlock.hidden = true;
      // A moment to read the line about the card, then go. The button is there for anybody whose
      // browser blocks the automatic move, which is the whole reason it is a real link.
      setTimeout(function () { if (dest) location.href = dest; }, 1400);
    } else {
      // No plan chosen yet: they pick here, and every link is rewritten so the email is prefilled
      // and the ad click still rides through to Stripe.
      if (plansBlock) {
        Array.prototype.forEach.call(plansBlock.querySelectorAll('a[href*="buy.stripe.com"]'), function (a) {
          a.setAttribute('href', checkoutUrl(a.getAttribute('href'), payload.email));
        });
        plansBlock.hidden = false;
      }
      if (contBlock) contBlock.hidden = true;
    }

    var h = stepDone.querySelector('.sw-leadpop__title');
    if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    errBox.hidden = true;
    Array.prototype.forEach.call(form.querySelectorAll('[aria-invalid]'), function (el) {
      el.removeAttribute('aria-invalid');
    });

    var name = val('contact[name]');
    var shop = val('contact[shop name]');
    var email = val('contact[email]');
    var phone = val('contact[phone]');

    if (!name) return fail('Your name, so I know who I am talking to.', 'contact[name]');
    if (!shop) return fail('What is the shop called?', 'contact[shop name]');
    if (!email || !/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(email)) {
      return fail('That email does not look right.', 'contact[email]');
    }
    // The number is required because the whole offer is that somebody rings them to book the
    // walkthrough. Agreeing to MARKETING texts is the thing that stays optional, and that is the
    // checkbox, not this field.
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      return fail('A number we can reach you on to set the time.', 'contact[phone]');
    }

    var smsBox = form.querySelector('[data-consent-sms]');
    var fine = dlg.querySelector('[data-sw-leadpop-fine]');
    // The wording as it stood today, stored with the row. What somebody agreed to in September
    // must not become whatever the popup says in March.
    var wording = [];
    if (smsBox && smsBox.checked) wording.push(smsBox.parentNode.textContent.trim());
    if (fine) wording.push(fine.textContent.replace(/\s+/g, ' ').trim());

    // Mark where it came from so popup leads are tellable from page-form leads in the console,
    // and flag the ad ones. sw-click-attribution parks the click id in a cookie on arrival.
    // Both click ids ride along in the message so a lead can be traced to the ad that made it
    // even before the Stripe webhook sees anything. Google's rides on to Stripe as the reference;
    // Meta's cannot (Stripe only takes one), so recording it here is the only place it survives.
    var gClick = (document.cookie.match('(^|;)\\s*sw_click_id\\s*=\\s*([^;]+)') || [])[2] || '';
    var fbClick = (document.cookie.match('(^|;)\\s*sw_fb_click_id\\s*=\\s*([^;]+)') || [])[2] || '';
    var fromAd = !!(gClick || fbClick);
    var payload = {
      name: name,
      shop_name: shop,
      email: email,
      phone: phone,
      techs: val('contact[techs]'),
      /**
       * WHAT ACTUALLY HAPPENED, WHICH IS NOT YET A TRIAL.
       *
       * This line is written the moment the popup form is submitted -- before the person has seen
       * the card form, let alone finished it. It used to read "Started a free trial from the
       * popup", and the lead email repeats it verbatim, so Arturo Roman (2026-09-12) arrived in
       * Kevin's inbox as a new trial and then could not be found anywhere, because he clicked
       * through to Stripe and never entered a card. The checkout session expired unpaid; no shop
       * was ever created. Nothing was broken except this sentence.
       *
       * So it says what is true at the instant it is written, and names the one thing that proves
       * the rest: a shop appearing in the console.
       */
      message: 'Asked for a free trial from the popup'
        + ((pending && pending.plan) ? ', plan: ' + pending.plan : ', plan not chosen yet')
        + '. Sent on to the card form -- they have only actually started one if a shop appears in the console.'
        + (fbClick ? ' fbclid=' + fbClick : '') + (gClick ? ' gclid=' + gClick : ''),
      email_consent: true,
      sms_consent: !!(smsBox && smsBox.checked),
      consent_wording: wording.join('\n\n') || null,
      page: (path + ' (popup)' + (fromAd ? ' [ad]' : '')).slice(0, 300),
      referrer: document.referrer || null,
      company_website: val('company_website'),
      elapsed_ms: Date.now() - startedAt
    };

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    alsoEmailKevin(payload);

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    })
      .then(function (r) { return r.ok ? r.json() : r.json().then(function (j) { throw new Error(j.error || 'could not save'); }); })
      .then(function () { finish(payload); })
      .catch(function (err) {
        // Shopify already has it, so this is only ever a wording problem for the person in front
        // of us. Do not tell them it failed when it did not, and never hold up the checkout.
        if (window.console) console.warn('[leadpop]', err && err.message);
        finish(payload);
      });

    // The hand-off does not wait on our own network. Whichever lands first, the buyer moves.
    setTimeout(function () { finish(payload); }, HANDOFF_MS);
  });
})();
